import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';

import { selectIsUuidBasedProject } from 'core/userContext/selectors';
import { ESFieldTypeMap } from 'modules/workflows/types/fields';
import { useFieldMappingsContext } from 'modules/workflows/context/FieldMappingsContext';
import {
  fetchEventFieldMappingsRequest,
  getEventFieldMappings,
  getIsFetchingEventFieldMappings,
  getIsJourneyFieldMappingsEnabled,
} from 'modules/workflows/ducks';
import { EVENT_TYPES } from 'utils/dataTypes';
import { ContainsCloningError, SearchQueriesValidationErrors } from '../RuleBuilder/types';
import { FilterSegmentSelectInputOption, PropertyMapConfig, Query } from './FilterInputsList.types';
import {
  createPropertyItemFromQuery,
  flushTimer,
  getPropertyMap,
  getPropertyOptions,
  shouldAddFieldToPropertyMap,
} from './FilterInputsList.utils';

type UsePropertiesArguments = {
  dataType: string;
  initialQueries: Query[];
  queries: Partial<Query>[];
  onSearchQueriesUpdate: (queries: Partial<Query>[]) => void;
  queriesErrors?: SearchQueriesValidationErrors;
  propertyMapDisallowedIds?: string[];
  filterPropertyMap?: (map: ESFieldTypeMap, config?: PropertyMapConfig) => ESFieldTypeMap;
  propertyMapConfig?: PropertyMapConfig;
};

const useProperties = ({
  dataType,
  propertyMapDisallowedIds,
  onSearchQueriesUpdate,
  queries,
  initialQueries,
  filterPropertyMap,
  propertyMapConfig,
}: UsePropertiesArguments) => {
  const dispatch = useDispatch();

  const [isShown, setIsShown] = useState(false);
  const [propertyMap, setPropertyMap] = useState<ESFieldTypeMap | null>(null);
  const [propertyOptions, setPropertyOptions] = useState<FilterSegmentSelectInputOption[] | null>(
    null,
  );
  const loadingTimeoutRef = useRef<number | null>(null);
  const propertyTimeoutRef = useRef<number | null>(null);
  // Event field mappings, needed for filters
  const isJourneyFieldMappingsEnabled = useSelector(getIsJourneyFieldMappingsEnabled);
  const { fieldMappings, isLoading } = useFieldMappingsContext();
  const mappingsOld = useSelector(getEventFieldMappings);
  const mappings = isJourneyFieldMappingsEnabled ? fieldMappings : mappingsOld;

  const isFetchingOld = useSelector(getIsFetchingEventFieldMappings);
  const isFetchingEventFieldMappings = isJourneyFieldMappingsEnabled ? isLoading : isFetchingOld;

  const isUUIDProject = useSelector(selectIsUuidBasedProject);
  const updatePropertyMap = (map: ESFieldTypeMap) => {
    setPropertyMap(map);
    setPropertyOptions(getPropertyOptions(dataType, map, propertyMapDisallowedIds));
  };

  const handleQueryUpdate = (q: Partial<Query> & ContainsCloningError = {}, i: number) => {
    // Adds query field to property list if it is newly added from typeahead
    if (
      dataType === EVENT_TYPES.CUSTOM_EVENT &&
      shouldAddFieldToPropertyMap(q, propertyMap as ESFieldTypeMap)
    ) {
      updatePropertyMap({ ...propertyMap, [q.field as string]: createPropertyItemFromQuery(q) });
    }
    const query = cloneDeep(q);
    const hasCloningErrors = query?.containsCloningError === true;
    const hasValidValue = query?.value !== null;
    // if there is a valid value, remove the cloning error flag for this specific query
    if (hasCloningErrors && hasValidValue) {
      delete query?.containsCloningError;
    }
    onSearchQueriesUpdate([...queries.slice(0, i), query, ...queries.slice(i + 1)]);
  };

  useEffect(() => {
    if (!isJourneyFieldMappingsEnabled) dispatch(fetchEventFieldMappingsRequest());

    // cleanup timeouts
    return () => {
      flushTimer(loadingTimeoutRef.current);
      flushTimer(propertyTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isEmpty(mappings)) {
      flushTimer(propertyTimeoutRef.current);
      /**
       * Putting this in a timeout makes sure that all this work is done after the
       * component fully mounts. This gives the appearance of this loading
       * quicker, as the work is not required for the first render.
       * (https://iterable.atlassian.net/browse/WF-3312)
       */
      propertyTimeoutRef.current = window.setTimeout(() => {
        // adds campaign label field that doesnt come back from event field mappings
        // also adds any custom-input values provided for the customEvent mapping (ie: API Call entry source)
        const map = getPropertyMap({
          dataType,
          mappings,
          initialQueries,
          currentQueries: queries,
          isUUIDProject,
        });
        updatePropertyMap(map);
        setIsShown(true);
      });
    }
  }, [mappings, dataType]);

  useEffect(() => {
    if (!isShown && !isFetchingEventFieldMappings && propertyOptions && propertyMap) {
      flushTimer(loadingTimeoutRef.current);
      /**
       * Hack to make selects load after the rest of the editor, which helps fix
       * the start node from loading really slowly when there are a lot of
       * fields (https://iterable.atlassian.net/browse/WF-3312)
       */
      loadingTimeoutRef.current = window.setTimeout(() => setIsShown(true));
    }
  }, [propertyOptions, propertyMap, isFetchingEventFieldMappings]);

  // useMemo might help here, but the dependency array is not obvious. Getting it wrong breaks Cypress tests, too.
  let filteredPropertyMap = propertyMap;
  let filteredPropertyOptions = propertyOptions;

  if (propertyMap && filterPropertyMap && !propertyMapConfig) {
    filteredPropertyMap = filterPropertyMap(propertyMap);
    filteredPropertyOptions = getPropertyOptions(
      dataType,
      filteredPropertyMap,
      propertyMapDisallowedIds,
    );
  }

  if (propertyMap && filterPropertyMap && propertyMapConfig) {
    filteredPropertyMap = filterPropertyMap(propertyMap, propertyMapConfig);
    filteredPropertyOptions = getPropertyOptions(dataType, filteredPropertyMap);
  }
  return {
    isShown,
    propertyMap: filteredPropertyMap,
    propertyOptions: filteredPropertyOptions,
    handleQueryUpdate,
  };
};

export { useProperties };
